import React, { useEffect } from 'react';
import { Flex, Form, Input, InputNumber, Modal, Select, Switch, TimePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ChannelEntity } from '../../../domain/entities/Channel';
import { ChannelCategoryEntity } from '../../../domain/entities/ChannelCategory';
import { ChannelRuleEntity } from '../../../domain/entities/ChannelRule';
import { ChannelLink } from '../../../domain/value-objects/ChannelLink';
import {
  MAX_CHANNEL_SENDING_INTERVAL_MINUTES,
  MIN_CHANNEL_SENDING_INTERVAL_MINUTES,
} from '../../../domain/services/ChannelAdministrationRules';
import { SendingTime } from '../../../domain/value-objects/SendingTime';
import { ChannelType } from '../../../shared/types/Channel';
import ChannelRulesField from './ChannelRulesField.component';

export interface AdminChannelFormValues {
  link: string;
  type: ChannelType;
  categoryUuid?: string;
  membersCount: number;
  minimalSendingInterval: number;
  isPrivate: boolean;
  sendingStartTime?: string;
  sendingEndTime?: string;
  rules?: ChannelRuleEntity[];
}

interface AdminChannelFormModalProps {
  open: boolean;
  channel?: ChannelEntity;
  categories: ChannelCategoryEntity[];
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (values: AdminChannelFormValues) => Promise<void>;
}

const DEFAULT_SENDING_INTERVAL_MINUTES = 30;
const SENDING_TIME_FORMAT = 'HH:mm';

const toPickerValue = (value?: string) => ({ value: value ? dayjs(value, SENDING_TIME_FORMAT) : null });
const fromPickerValue = (value: Dayjs | null) => (value ? value.format(SENDING_TIME_FORMAT) : undefined);

const AdminChannelFormModal: React.FC<AdminChannelFormModalProps> = ({ open, channel, categories, isSaving, onCancel, onSubmit }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<AdminChannelFormValues>();
  const isEdit = Boolean(channel);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(channel
      ? {
        link: channel.link,
        type: channel.type,
        categoryUuid: channel.category?.uuid,
        membersCount: channel.membersCount,
        minimalSendingInterval: channel.minimalSendingInterval,
        isPrivate: channel.isPrivate,
        sendingStartTime: channel.sendingWindow.start,
        sendingEndTime: channel.sendingWindow.end,
        rules: channel.rules,
      }
      : {
        type: ChannelType.FREE,
        membersCount: 0,
        minimalSendingInterval: DEFAULT_SENDING_INTERVAL_MINUTES,
        isPrivate: false,
      });
  }, [open, channel, form]);

  const handleValuesChange = (changed: Partial<AdminChannelFormValues>) => {
    if (isEdit || changed.link === undefined || form.isFieldTouched('isPrivate')) return;
    form.setFieldValue('isPrivate', ChannelLink.isPrivate(changed.link));
  };

  const typeOptions = [
    { value: ChannelType.FREE, label: t('channel-type-free', { ns: 'labels' }) },
    { value: ChannelType.PAID, label: t('channel-type-paid', { ns: 'labels' }) },
    { value: ChannelType.DEACTIVATED, label: t('channel-type-deactivated', { ns: 'labels' }) },
  ];

  return (
    <Modal
      open={open}
      title={isEdit ? t('channel-edit', { ns: 'titles' }) : t('channel-create', { ns: 'titles' })}
      okText={isEdit ? t('save', { ns: 'buttons' }) : t('create', { ns: 'buttons' })}
      cancelText={t('cancel', { ns: 'buttons' })}
      confirmLoading={isSaving}
      onCancel={onCancel}
      onOk={() => form.submit()}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onSubmit} onValuesChange={handleValuesChange} requiredMark={false}>
        <Form.Item
          name="link"
          label={t('channel-link', { ns: 'labels' })}
          extra={isEdit ? t('channel-link-readonly-hint', { ns: 'labels' }) : undefined}
          rules={[{ required: true, whitespace: true, message: t('channel-link-required', { ns: 'errors' }) }]}
        >
          <Input placeholder={t('channel-link-placeholder', { ns: 'labels' })} disabled={isEdit} autoFocus={!isEdit} />
        </Form.Item>
        <Form.Item name="type" label={t('channel-type', { ns: 'labels' })}>
          <Select options={typeOptions} />
        </Form.Item>
        <Form.Item name="categoryUuid" label={t('channel-category', { ns: 'labels' })}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('channel-no-category', { ns: 'labels' })}
            options={categories.map((c) => ({ value: c.uuid, label: c.name }))}
          />
        </Form.Item>
        <Form.Item
          name="membersCount"
          label={t('channel-members-count', { ns: 'labels' })}
          rules={[{ required: true, type: 'integer', min: 0, message: t('channel-members-count-negative', { ns: 'errors' }) }]}
        >
          <InputNumber min={0} precision={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="minimalSendingInterval"
          label={t('channel-sending-interval', { ns: 'labels' })}
          rules={[{
            required: true,
            type: 'integer',
            min: MIN_CHANNEL_SENDING_INTERVAL_MINUTES,
            max: MAX_CHANNEL_SENDING_INTERVAL_MINUTES,
            message: t('channel-minimal-sending-interval-out-of-range', { ns: 'errors' }),
          }]}
        >
          <InputNumber
            min={MIN_CHANNEL_SENDING_INTERVAL_MINUTES}
            max={MAX_CHANNEL_SENDING_INTERVAL_MINUTES}
            precision={0}
            addonAfter={t('schedule-interval-minutes', { ns: 'labels' })}
            style={{ width: '100%' }}
          />
        </Form.Item>
        {isEdit && (
          <Form.Item
            label={t('channel-sending-window', { ns: 'labels' })}
            extra={t('channel-sending-window-hint', { ns: 'labels' })}
          >
            <Flex gap={12}>
              <Form.Item
                name="sendingStartTime"
                noStyle
                getValueProps={toPickerValue}
                normalize={fromPickerValue}
                rules={[{ required: true, message: t('channel-sending-window-invalid', { ns: 'errors' }) }]}
              >
                <TimePicker
                  format={SENDING_TIME_FORMAT}
                  allowClear={false}
                  needConfirm={false}
                  placeholder={t('channel-sending-window-start', { ns: 'labels' })}
                  style={{ flex: 1 }}
                />
              </Form.Item>
              <Form.Item
                name="sendingEndTime"
                noStyle
                dependencies={['sendingStartTime']}
                getValueProps={toPickerValue}
                normalize={fromPickerValue}
                rules={[
                  { required: true, message: t('channel-sending-window-invalid', { ns: 'errors' }) },
                  ({ getFieldValue }) => ({
                    validator: (_, value?: string) => {
                      const start = getFieldValue('sendingStartTime');
                      if (!value || !start || SendingTime.isBefore(start, value)) return Promise.resolve();
                      return Promise.reject(new Error(t('channel-sending-window-invalid', { ns: 'errors' })));
                    },
                  }),
                ]}
              >
                <TimePicker
                  format={SENDING_TIME_FORMAT}
                  allowClear={false}
                  needConfirm={false}
                  placeholder={t('channel-sending-window-end', { ns: 'labels' })}
                  style={{ flex: 1 }}
                />
              </Form.Item>
            </Flex>
          </Form.Item>
        )}
        {isEdit && <ChannelRulesField />}
        <Form.Item
          name="isPrivate"
          label={t('channel-is-private', { ns: 'labels' })}
          extra={isEdit ? undefined : t('channel-is-private-hint', { ns: 'labels' })}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AdminChannelFormModal;
